/**
 * Пришли деньги: куда их деть (блок B-12).
 *
 * Очередь одна и та же у всех: сначала платежи до следующего прихода, потом
 * жизнь на этот срок, и только остаток — целям. Порядок не настройка, а смысл:
 * цель, наполненная раньше платежа, вернётся дефицитом через неделю.
 *
 * Три правила экрана, каждое из плана §12.4.
 *
 * · **Обеспеченное не резервируется повторно.** Берётся непокрытая часть
 *   платежа, и только один раз.
 * · **При неизвестных расходах автоматического распределения нет.** Остаток не
 *   называется свободным: неизвестно, что из него — деньги повседневные нужды. Человек
 *   может назвать сумму сам, и тогда расчёт идёт от неё.
 * · **Принятие ≠ оплата.** Записывается приход и резервы под платежи; сами
 *   платежи остаются неоплаченными, пока человек не подтвердит каждый.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ZERO, confirmed, diffDays, distributeIncome, formatMoney, nextIncomeDate, pd,
  type DistributionResult, type Money, type PlainDate,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { advisorGate, fetchAccess, onAccessChanged, UNKNOWN_ACCESS, type AccessState } from './access.js';
import { AccountField } from './operation-fields.js';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { parseAmountInput, todayPd, type AppState } from './store.js';
import { dayMonth } from './text.js';
import { DateButton } from './date-picker.js';

export function DistributeIncome({ state, incomeStreamId, onClose, onApply, onDiscuss }: {
  state: AppState;
  incomeStreamId: string;
  onClose: () => void;
  /**
   * Обсудить раскладку со SKOKA. Доступно в Pro и пробном периоде.
   *
   * Пересчёт и ручное распределение — бесплатные и остаются на месте: платной
   * становится не раскладка, а разговор о ней.
   */
  onDiscuss?: (question: string) => void;
  /** Записать приход и закрепить деньги под названные платежи. */
  onApply: (plan: {
    amount: Money;
    accountId: string;
    /** Откуда деньги — имя дохода, как его видит человек в истории. */
    source: string;
    /** Дата прихода: от неё считается, какие назначения пора исполнять. */
    date: PlainDate;
    reserves: { obligationId: string; instanceId: string; amount: Money }[];
    goals: { goalId: string; amount: Money }[];
  }) => Promise<void>;
}) {
  const today = todayPd();
  const stream = state.incomeStreams.find((s) => s.id === incomeStreamId);
  const [raw, setRaw] = useState(stream ? String(stream.amount / 100) : '');
  /** Названная человеком сумма повседневные нужды, если приложение её не знает. */
  const [livingRaw, setLivingRaw] = useState('');
  const [keep, setKeep] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [access, setAccess] = useState<AccessState>(UNKNOWN_ACCESS);
  useEffect(() => { void fetchAccess().then(setAccess); }, []);
  // Включили пробный или оплатили — метки «Pro» снимаются сразу, без перезагрузки.
  useEffect(() => onAccessChanged(() => { void fetchAccess().then(setAccess); }), []);
  /*
   * Реквизиты прихода — на виду и правятся до записи: на какой счёт, когда и
   * от кого. Раньше они подставлялись молча, и человек подтверждал запись,
   * которой не видел целиком.
   */
  const spendable = state.accounts.filter((a) => !a.archived && a.kind !== 'credit_card');
  const [accountId, setAccountId] = useState(
    spendable.find((a) => a.id === stream?.accountId)?.id ?? spendable.find((a) => a.isLiquid)?.id
      ?? spendable[0]?.id ?? '',
  );
  const [date, setDate] = useState<PlainDate>(today);
  /**
   * Этот доход уже записан в последние дни. Тогда «Пришли деньги» скорее
   * всего повтор: тот же приход попал бы в историю дважды.
   */
  const already = stream ? state.transactions.find((t) => t.type === 'income'
    && t.link?.kind === 'income' && t.link.incomeStreamId === stream.id
    && diffDays(t.occurredDate as PlainDate, today) <= 5) : undefined;

  /**
   * Срок, названный человеком, когда даты прихода нет.
   *
   * Пусто — периода нет, и расчёт остаётся ограниченным. Это не поле «на
   * всякий случай»: без срока неизвестно, сколько жизни защищать, и молча
   * подставить месяц значит снова раскладывать деньги по выдуманному окну.
   */
  const [protectDays, setProtectDays] = useState<number | null>(null);
  const amount = parseAmountInput(raw);
  /**
   * Названная человеком сумма повседневные нужды.
   *
   * `null` — он её не называл. Пустая строка и подтверждённый ноль — разные
   * ответы: «не знаю» и «мне не на что тратить» ведут к разным расчётам.
   */
  const assumed = livingRaw.trim() === '' ? null : parseAmountInput(livingRaw);

  const result = useMemo((): DistributionResult | null => {
    if (amount <= ZERO) return null;
    return distributeIncome({
      amount,
      today,
      nextIncomeDate: nextIncome(state, today),
      obligations: state.obligations,
      reserves: state.reserves,
      // Названная сумма — такой же ответ человека, как введённая в настройке:
      // от неё расчёт перестаёт быть предварительным. Ноль здесь тоже ответ.
      needBaseline: assumed !== null ? confirmed(assumed) : state.baselines.need,
      goals: state.goals,
      accounts: state.accounts,
      creditTerms: state.creditTerms,
      loans: state.loans,
      monthlyNeed: assumed ?? state.needBaseline,
      ...(state.tradeoffChoice ? { choice: state.tradeoffChoice } : {}),
      ...(protectDays !== null ? { protectDays } : {}),
    });
  }, [state, amount, assumed, today, protectDays]);

  const apply = async (): Promise<void> => {
    if (!result) return;
    setBusy(true); setErr(null);
    try {
      await onApply({
        amount,
        accountId,
        source: stream?.name ?? 'Приход',
        date,
        // Принятые взносы — ровно те, что человек видел на экране.
        //
        // Раньше сохранялся только приход: цель оставалась с нулём, журнал
        // пустым, и «розданные» деньги назавтра снова числились свободными.
        goals: [
          ...(result.choice ? [{ goalId: result.choice.goalId, amount: result.choice.amount }] : []),
          ...result.goals.map((g) => ({ goalId: g.goalId, amount: g.amount })),
        ].filter((g) => g.amount > ZERO),
        // Закрепляем только то, что человек оставил отмеченным: предложение —
        // не решение, и снятая галочка должна что-то значить.
        reserves: result.obligations
          .filter((o) => keep[o.instanceId] !== false)
          .map((o) => ({
            obligationId: o.instanceId.slice(0, o.instanceId.lastIndexOf(':')),
            instanceId: o.instanceId,
            amount: o.amount,
          })),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось записать');
      setBusy(false);
    }
  };

  return (
    <SheetWrap title="Пришли деньги" onClose={onClose}>
      <div className="form">
        <label>Сколько пришло
          <AmountInput value={raw} onChange={setRaw} placeholder="40 000" /></label>
      </div>
      <div className="oprows">
        <AccountField label="Счёт" pickerTitle="Зачислить на счёт" accounts={spendable}
          value={accountId} onChange={setAccountId} />
        <div className="oprow">Когда пришло
          <span className="opval">
            {date === today ? 'Сегодня' : dayMonth(date, today)}
            <DateButton overlay label="Когда пришло" value={date} max={today} onChange={(d) => setDate(pd(d))} />
          </span>
        </div>
        {stream && <div className="oprow">Доход<span className="opval plain">{stream.name}</span></div>}
      </div>
      {already && (
        <div className="note warn">
          «{stream!.name}» уже записан {dayMonth(already.occurredDate, today)} —{' '}
          {formatMoney(already.amount)}. Если это тот же приход, записывать его второй
          раз не нужно.
        </div>
      )}

      {result === null ? (
        <div className="note">Назови сумму — разложу её по очереди.</div>
      ) : (
        <>
          <div className="note" style={{ marginTop: 0 }}>
            {protectNote(result, today)}
          </div>

          {/*
            Даты нет — спрашиваем срок, а не придумываем его.

            Здесь раньше молча брался месяц: приложение писало «дата
            неизвестна», и всё равно раскладывало деньги так, будто срок
            известен. Теперь до ответа расчёт честно ограничен, а ответ —
            это один тап.
          */}
          {result.protect.source === 'unknown' && (
            <div className="pick"><div className="k">На сколько растянуть эти деньги?</div>
              <div className="chips">
                {[7, 14, 30].map((d) => (
                  <button key={d} className="chip" onClick={() => setProtectDays(d)}>
                    {d === 7 ? 'на неделю' : d === 14 ? 'на две недели' : 'на месяц'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {result.obligations.length > 0 && (
            <>
              {/* Принятие распределения — не оплата: платежи остаются
                  неоплаченными, пока человек не подтвердит каждый отдельно. */}
              <div className="blkhead"><b>Сначала платежи
                <Tip>Отмеченные суммы закреплю под платежи — оплаченными они от этого
                  не станут: подтвердить оплату нужно, когда деньги правда уйдут.</Tip></b>
                <span className="tag soft">{result.obligations.length}</span></div>
              {result.obligations.map((o) => (
                <label className="li paidrow" key={o.instanceId}>
                  <input type="checkbox" checked={keep[o.instanceId] !== false}
                    onChange={(e) => setKeep((p) => ({ ...p, [o.instanceId]: e.target.checked }))} />
                  <div className="nm"><b>{o.label}</b><span>{dayMonth(o.dueDate, today)} · не обеспечен</span></div>
                  <div className="am num">{formatMoney(o.amount)}</div>
                </label>
              ))}
            </>
          )}

          {/* Жизнь — не остаток в конце, а такая же строка очереди: иначе
              «свободные деньги» каждый раз оказываются деньгами на еду. */}
          <div className="row"><div className="rl">Повседневные нужды{result.protect.until ? ` до ${dayMonth(result.protect.until, today)}` : ''}
            <span className="rn">
              {result.living === null ? 'не указано' : formatMoney(result.living)}
            </span>
          </div></div>

          {/*
            Поле живёт независимо от того, посчитан ли уже расчёт.
            
            Раньше оно существовало только при `preliminary`: первая же цифра
            делала расходы «известными», поле исчезало из-под пальца, и на
            десять дней защищались 2 ₽. Ввод — это ввод, а не подтверждение.
          */}
          {!state.baselines.need.known && (
            <>
              {result.preliminary && (
                <div className="note warn">
                  {result.unknowns[0]?.reason ?? 'Обычные траты неизвестны'}.
                  Пока их нет, цели автоматически не наполняю, а остаток не называю
                  свободным: неизвестно, что из него — деньги повседневные нужды.
                </div>
              )}
              <div className="form">
                <label>Сколько примерно уходит на повседневные нужды в месяц
                  <Tip>Назовёшь — разложу остаток по целям прямо сейчас. Можно и не
                    называть: платежи закреплю всё равно.</Tip>
                  <AmountInput value={livingRaw} onChange={setLivingRaw}
                    placeholder="30 000" /></label>
              </div>
            </>
          )}

          {result.preliminary ? null : (
            <>
              {result.choice && (
                <div className="li"><div className="nm">
                  <b>{result.choice.goalName}</b><span>{result.choice.reason}</span>
                </div><div className="am num">{formatMoney(result.choice.amount)}</div></div>
              )}
              {result.goals.length > 0 && (
                <>
                  <div className="blkhead"><b>Потом цели</b></div>
                  {result.goals.map((g) => (
                    <div className="li" key={g.goalId}><div className="nm">
                      <b>{g.goalName}</b><span>{g.reason}</span>
                    </div><div className="am num">{formatMoney(g.amount)}</div></div>
                  ))}
                </>
              )}
              <div className="row lvl-subtotal"><div className="rl">Остаётся свободным
                <span className="rn">{formatMoney(result.leftover)}</span>
              </div></div>
            </>
          )}

          {result.shortfall > ZERO && (
            <div className="note alarm">
              Не хватает {formatMoney(result.shortfall)} на обязательные платежи
              {result.protect.until ? ` до ${dayMonth(result.protect.until, today)}` : ''}. Цели не наполняю: сначала платежи.
            </div>
          )}

          {err && <div className="note warn">{err}</div>}

          <button className="btn pri" disabled={busy || !accountId} onClick={() => void apply()}>
            {busy ? 'Записываю…' : already ? 'Записать ещё один приход' : 'Записать приход и закрепить деньги'}
          </button>
          <div className="note">
            Запишу приход {formatMoney(amount)} на «{spendable.find((a) => a.id === accountId)?.name ?? 'счёт'}»
            {' '}{date === today ? 'сегодня' : dayMonth(date, today)}. Отмеченные суммы закреплю под
            платежи.
          </div>

          {/*
            Разговор о раскладке — платная часть, сама раскладка — нет.

            Пересчёт после прихода и ручное распределение доступны всем: это
            расчёты приложения. Платным становится только обсуждение их с
            моделью, и предлагать его тому, у кого его нет, незачем.
          */}
          {onDiscuss && advisorGate(access).kind !== 'signup' && (
            <button className="btn" onClick={() => onDiscuss(
              `Пришло ${formatMoney(amount)}. Как лучше разложить?`,
            )}>
              Обсудить со SKOKA
            </button>
          )}
        </>
      )}
    </SheetWrap>
  );
}

/** Период защиты называется вместе с его источником — это не обещание. */
function protectNote(r: DistributionResult, today: PlainDate): string {
  const days = `${r.protect.days} дн.`;
  if (r.protect.source === 'horizon') {
    return `Считаю до ближайшего прихода — ${r.protect.until ? dayMonth(r.protect.until, today) : ''} (${days}).`;
  }
  if (r.protect.source === 'user') return `Считаю на выбранный период: ${days}.`;
  return 'Дата следующего прихода неизвестна. Пока не назовёшь срок, закрою только '
    + 'наступившие платежи — раскладывать остальное не от чего.';
}

/**
 * Дата ближайшего прихода.
 *
 * `null` — её нет, и выдумывать нельзя: от этой даты зависит период защиты, а
 * выдуманная дата делает защиту короче, чем нужно.
 *
 * Прежде отсюда выбрасывались потоки с `isVariable`, и комментарий утверждал,
 * что «у плавающего дохода даты нет». Это смешивало две разные вещи: `isVariable`
 * означает **плавающую сумму**, а не дату. Зарплата приходит ровно 5-го и
 * каждый раз разной суммой — дата у неё есть, и она надёжна.
 *
 * Цена ошибки видна на данных владельца: оба его дохода помечены плавающей
 * суммой, поэтому распределение не находило ни одной даты и защищало жизнь на
 * выдуманные 30 дней вместо 14 до реального прихода. Главная при этом
 * считалась правильно — `computeAvailable` этот фильтр не применяет, — и два
 * экрана расходились на ровном месте.
 *
 * Осторожность к самой сумме от этого не теряется: у плавающего дохода в
 * расчёт идёт нижняя оценка (`conservativeIncome`), а не обещанная сумма.
 */
export function nextIncome(state: AppState, today: PlainDate): PlainDate | null {
  return nextIncomeDate(state.incomeStreams, today);
}
